import {useState, useCallback} from "react";
import {House, AlertCircle} from "lucide-react";
import * as m from "@/paraglide/messages";
import {MainFilters} from "@/components/personas/main-filters";
import {PersonasGrid} from "@/components/personas/personas-grid";
import {PersonaModal, type PersonaModalTab} from "@/components/personas/persona-modal";
import {PlatformDock} from "@/components/dock/platform-dock";
import {
  useGeneratePersonas,
  usePlatforms,
  extractAiPersona,
  filterStateToRequest,
  DEFAULT_FILTER_STATE,
  type PersonasFilterState,
} from "@/hooks/use-ai-personas";
import {useCreateFavorite, useFavorites} from "@/hooks/use-favorites";
import type {AiPersonaResponse, AiPersonaGenerationResponse} from "@/lib/personas-api";
import {Button} from "@/components/ui/button";
import {SentimentInfluencersSection} from "@/components/influencers/sentiment-influencers-section";
import {ScrollArea} from "@/components/ui/scroll-area";

export function MainPage() {
  const [filters, setFilters] = useState<PersonasFilterState>(DEFAULT_FILTER_STATE);
  const [generationResult, setGenerationResult] = useState<AiPersonaGenerationResponse | null>(
    null
  );

  // Modal state - track full AiPersonaResponse (with DB ID) instead of just AiPersona
  const [selectedPersonaResponse, setSelectedPersonaResponse] = useState<AiPersonaResponse | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState<PersonaModalTab>("details");

  const {data: platforms, isLoading: platformsLoading} = usePlatforms();
  const generateMutation = useGeneratePersonas();
  const createFavoriteMutation = useCreateFavorite();

  // Fetch favorites to check if persona is already favorited
  const {data: favoritesData} = useFavorites("persona");

  const handleGenerate = useCallback(() => {
    const request = filterStateToRequest(filters);
    generateMutation.mutate(request, {
      onSuccess: (data) => {
        setGenerationResult(data);
      },
    });
  }, [filters, generateMutation]);

  const handlePersonaClick = useCallback(
    (personaResponse: AiPersonaResponse) => {
      createFavoriteMutation.reset();
      setSelectedPersonaResponse(personaResponse);
      setModalTab("details");
      setIsModalOpen(true);
    },
    [createFavoriteMutation]
  );

  const handlePersonaChatClick = useCallback(
    (personaResponse: AiPersonaResponse) => {
      createFavoriteMutation.reset();
      setSelectedPersonaResponse(personaResponse);
      setModalTab("chat");
      setIsModalOpen(true);
    },
    [createFavoriteMutation]
  );

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleAddToFavorites = useCallback(() => {
    if (!selectedPersonaResponse) return;

    createFavoriteMutation.mutate({
      entity_type: "persona",
      entity_id: selectedPersonaResponse.id,
    });
  }, [selectedPersonaResponse, createFavoriteMutation]);

  const handlePlatformChange = useCallback((platformIds: string[]) => {
    setFilters((prev) => ({...prev, platformIds}));
  }, []);

  // Check if selected persona is already favorited
  const isSelectedPersonaFavorited = selectedPersonaResponse
    ? (favoritesData?.favorites.some((f) => f.entity_id === selectedPersonaResponse.id) ?? false)
    : false;

  // Extract AiPersona from response for modal display
  const selectedPersona = selectedPersonaResponse
    ? extractAiPersona(selectedPersonaResponse)
    : null;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header + Filters */}
      <div className="shrink-0 space-y-6 p-4 pb-0 sm:p-6 sm:pb-0">
        <div className="flex items-center gap-3">
          <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-lg">
            <House className="text-primary h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-semibold">{m.main_page_title()}</h1>
            <p className="text-muted-foreground text-sm">{m.main_page_description()}</p>
          </div>
        </div>

        <MainFilters filters={filters} onFiltersChange={setFilters} />
      </div>

      {/* Content Area */}
      <ScrollArea className="min-h-0 flex-1">
        <div className="p-4 pb-28 sm:p-6 sm:pb-28">
          {/* Error State */}
          {generateMutation.isError && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="bg-destructive/10 mb-4 flex h-16 w-16 items-center justify-center rounded-full">
                <AlertCircle className="text-destructive h-8 w-8" />
              </div>
              <h3 className="mb-2 text-lg font-medium">{m.personas_error_title()}</h3>
              <p className="text-muted-foreground mb-4 max-w-md text-center text-sm">
                {generateMutation.error?.message || "An unexpected error occurred"}
              </p>
              <Button variant="outline" onClick={handleGenerate}>
                {m.personas_error_retry()}
              </Button>
            </div>
          )}

          {/* Grid with built-in empty/loading states */}
          {!generateMutation.isError && (
            <PersonasGrid
              personas={generationResult?.personas ?? []}
              isLoading={generateMutation.isPending}
              onPersonaClick={handlePersonaClick}
              onPersonaChatClick={handlePersonaChatClick}
              mentionCount={generationResult?.mention_count}
              onGenerate={handleGenerate}
            />
          )}

          {/* Sentiment Influencers Section */}
          <SentimentInfluencersSection locationId={filters.locationId} className="mt-8" />
        </div>
      </ScrollArea>

      {/* Persona Modal */}
      <PersonaModal
        persona={selectedPersona}
        aiPersonaId={selectedPersonaResponse?.id}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        activeTab={modalTab}
        onTabChange={setModalTab}
        locationId={filters.locationId}
        date={filters.date}
        onAddToFavorites={handleAddToFavorites}
        isAddingToFavorites={createFavoriteMutation.isPending}
        isFavorite={isSelectedPersonaFavorited || createFavoriteMutation.isSuccess}
      />

      {/* Platform Filter Dock */}
      {!platformsLoading && platforms && (
        <PlatformDock
          platforms={platforms}
          selectedIds={filters.platformIds}
          onSelectionChange={handlePlatformChange}
        />
      )}
    </div>
  );
}
